/**
 * SafePassage WebSocket Service
 *
 * This is the canonical implementation for SafePassage WebSocket communication.
 * All other components should use this service rather than implementing WebSocket logic directly.
 *
 * Features:
 * - Automatic reconnection with fixed interval
 * - Connection state management with proper lifecycle handling
 * - Message routing and type-safe callbacks
 * - Proper cleanup and resource management
 * - Frame streaming for real-time video analysis
 * - Comprehensive error handling and logging
 *
 * The service handles all WebSocket communication with the verify-engine, including
 * video frame streaming, feedback processing, and verification result handling.
 *
 * @author SafePassage Engineering
 * @version 1.0.0
 */

import { WEBSOCKET_CONNECTION, VIDEO_CAPTURE } from '../constants/websocket';
import { wsLogger } from '../utils/logger';
import type { WebSocketMessageBase } from '../types/websocket';
import type { VerificationResult } from '../types/verification';
import { analyticsService } from './AnalyticsService';
import { sanitizeVerificationResultForClient } from '../utils/verificationResultSanitizer';
import { attachVerificationOperationContext } from '../utils/verificationOperationContext';
import { withEngineRoutingKey } from '../utils/engineRouting';
import type { RecoverablePipelineErrorMessage } from '../types/pipelineRecovery';
import {
  isSelfieHandoffUnavailableMessage,
  PipelineRecoveryExhaustedError,
} from '../types/pipelineRecovery';

// Legacy export for backward compatibility
export const WEBSOCKET_CONSTANTS = {
  PING_INTERVAL_MS: WEBSOCKET_CONNECTION.PING_INTERVAL_MS,
  FRAME_RATE_MS: VIDEO_CAPTURE.FRAME_RATE_MS,
  CONNECTION_TIMEOUT_MS: WEBSOCKET_CONNECTION.CONNECTION_TIMEOUT_MS,
  MAX_RECONNECT_ATTEMPTS: WEBSOCKET_CONNECTION.MAX_RECONNECT_ATTEMPTS,
  RECONNECT_DELAY_MS: WEBSOCKET_CONNECTION.RECONNECT_DELAY_MS,
} as const;

export class SessionExpiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

export class SessionAuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SessionAuthError';
  }
}

// Types
/**
 * Configuration for WebSocket service initialization
 *
 * @interface WebSocketServiceConfig
 * @property {string} wsUrl - WebSocket server URL
 * @property {string} [sessionId] - Portal sessionId for consistent tracking
 * @property {Object} [config] - Additional configuration overrides
 * @property {boolean} [autoReconnect] - Enable automatic reconnection
 * @property {number} [maxReconnectAttempts] - Maximum reconnection attempts
 */
export interface WebSocketServiceConfig {
  wsUrl: string;
  sessionId?: string; // Portal sessionId for consistent tracking
  sessionToken?: string; // Session token for backend authentication
  lifecycleVersion?: number;
  workflowAttemptId?: string;
  workflowAttemptGeneration?: number;
  workflowAttemptVersion?: number;
  operationId?: string;
  operationGeneration?: number;
  config?: {
    emblem_state?: string;
    image_count?: number;
    interval_ms?: number;
    quality_threshold?: number;
    challenge_age?: number;
    test_mode?: boolean;
    warmup_period_ms?: number;
  };
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
}

/**
 * Callback functions for WebSocket events
 *
 * @interface WebSocketServiceCallbacks
 * @property {Function} [onConnectionEstablished] - Called when connection is established
 * @property {Function} [onFeedback] - Called when feedback message is received
 * @property {Function} [onCaptureProgress] - Called with capture progress updates
 * @property {Function} [onZoneTransition] - Called when transitioning between capture zones
 * @property {Function} [onCaptureStarted] - Called when capture session starts
 * @property {Function} [onProcessingStarted] - Called when processing begins
 * @property {Function} [onProcessingProgress] - Called with processing progress
 * @property {Function} [onVerificationResult] - Called with final verification result
 * @property {Function} [onNextCaptureCountdown] - Called with countdown to next capture
 * @property {Function} [onSessionStopped] - Called when session stops
 * @property {Function} [onDisconnecting] - Called when disconnecting
 * @property {Function} [onError] - Called on WebSocket errors
 * @property {Function} [onConnectionError] - Called on connection errors
 * @property {Function} [onReconnecting] - Called when attempting to reconnect
 * @property {Function} [onStopFrameCapture] - Called to stop frame capture
 * @property {Function} [onSessionRecovered] - Called when session is recovered
 */
export interface WebSocketServiceCallbacks {
  onConnectionEstablished?: (message: ConnectionEstablishedMessage) => void;
  onFeedback?: (message: FeedbackWebSocketMessage) => void;
  onCaptureProgress?: (message: CaptureProgressMessage) => void;
  onZoneTransition?: (message: ZoneTransitionMessage) => void;
  onCaptureStarted?: (message: CaptureStartedMessage) => void;
  onProcessingStarted?: (message: ProcessingMessage) => void;
  onProcessingProgress?: (message: ProcessingMessage) => void;
  onVerificationResult?: (message: VerificationResult) => void;
  onNextCaptureCountdown?: (message: NextCaptureMessage) => void;
  onSessionStopped?: (message: SessionStoppedMessage) => void;
  onDisconnecting?: (message: DisconnectingMessage) => void;
  onError?: (message: ErrorMessage) => void;
  onRecoverableError?: (message: RecoverablePipelineErrorMessage) => void;
  onConnectionError?: (error: Error) => void;
  onReconnecting?: (attempt: number) => void;
  onStopFrameCapture?: () => void;
  onSessionRecovered?: (sessionId: string) => void;
  onSessionRecoveryFailed?: (error: Error) => void;
}

// Message types (imported from existing types)
/**
 * WebSocket feedback message structure
 *
 * @interface FeedbackWebSocketMessage
 * @extends WebSocketMessageBase
 * @property {'feedback'} type - Message type identifier
 * @property {string} message - Feedback message text
 * @property {boolean} action_needed - Whether user action is required
 */
export interface FeedbackWebSocketMessage extends WebSocketMessageBase {
  type: 'feedback';
  message: string;
  action_needed: boolean;
}

/**
 * Capture progress message structure
 *
 * @interface CaptureProgressMessage
 * @extends WebSocketMessageBase
 * @property {'capture_progress'} type - Message type identifier
 * @property {number} captured - Number of frames captured
 * @property {number} total - Total frames needed
 * @property {number} quality_score - Current quality score
 */
export interface CaptureProgressMessage extends WebSocketMessageBase {
  type: 'capture_progress';
  captured: number;
  total: number;
  quality_score: number;
}

export interface ZoneTransitionMessage extends WebSocketMessageBase {
  type: 'zone_transition';
  message: string;
  zone: string;
  zone_index: number;
}

export interface CaptureStartedMessage extends WebSocketMessageBase {
  type: 'capture_started';
  target_images: number;
  interval_ms: number;
  test_mode?: boolean;
  zone_info?: {
    total_zones: number;
    current_zone: string;
    initial_message: string;
  };
}

export interface ProcessingMessage extends WebSocketMessageBase {
  type: 'processing_started' | 'processing_progress';
  message: string;
  step: string;
}

export interface NextCaptureMessage extends WebSocketMessageBase {
  type: 'next_capture_in';
  milliseconds: number;
}

export interface ErrorMessage extends WebSocketMessageBase {
  type: 'error';
  message: string;
  code?: string;
}

export interface ConnectionEstablishedMessage extends WebSocketMessageBase {
  type: 'connection_established';
  session_id: string;
  config: Record<string, unknown>;
}

export interface SessionStoppedMessage extends WebSocketMessageBase {
  type: 'session_stopped' | 'capture_stopped';
  message: string;
}

export interface DisconnectingMessage extends WebSocketMessageBase {
  type: 'disconnecting';
  reason: string;
}

// Re-export VerificationResult for backward compatibility
export type { VerificationResult } from '../types/verification';


export type SafePassageWebSocketMessage =
  | FeedbackWebSocketMessage
  | CaptureProgressMessage
  | ZoneTransitionMessage
  | CaptureStartedMessage
  | ProcessingMessage
  | NextCaptureMessage
  | ErrorMessage
  | ConnectionEstablishedMessage
  | SessionStoppedMessage
  | DisconnectingMessage
  | RecoverablePipelineErrorMessage
  | VerificationResult
;

/**
 * WebSocket service connection states
 *
 * @enum {string}
 */
export enum WebSocketServiceState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
}

/**
 * SafePassage WebSocket Service
 *
 * Manages all WebSocket communication with proper error handling, reconnection, and state management.
 *
 * This class provides:
 * - Reliable WebSocket connection management with automatic reconnection
 * - Video frame streaming capabilities for real-time analysis
 * - Message routing with type-safe callbacks
 * - Comprehensive error handling and logging
 * - Resource cleanup and memory management
 *
 * The service uses a fixed 3-second interval for reconnection attempts.
 */
export class SafePassageWebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketServiceConfig;
  private callbacks: WebSocketServiceCallbacks;
  private state: WebSocketServiceState = WebSocketServiceState.DISCONNECTED;
  private sessionId: string | null = null;
  private reconnectAttempts = 0;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private isManualDisconnect = false;
  private isCleaningUp = false;
  private hasRegisteredUnloadHandler = false;
  private pendingConnectReject: ((error: Error) => void) | null = null;
  private recoverableInterruptionActive = false;
  private verificationResultDelivered = false;

  constructor(
    config: WebSocketServiceConfig,
    callbacks: WebSocketServiceCallbacks = {}
  ) {
    this.config = config;
    this.callbacks = callbacks;

    // Register global beforeunload handler once
    this.registerUnloadHandler();
  }

  /**
   * Get current connection state
   *
   * @returns {WebSocketServiceState} Current connection state
   * @public
   */
  public getState(): WebSocketServiceState {
    return this.state;
  }

  /**
   * Get current session ID
   *
   * @returns {string | null} Current session ID or null if not connected
   * @public
   */
  public getSessionId(): string | null {
    return this.sessionId;
  }

  /**
   * Get current WebSocket URL
   *
   * @returns {string} WebSocket server URL
   * @public
   */
  public getWsUrl(): string {
    return this.config.wsUrl;
  }

  /**
   * Check if WebSocket is connected and ready
   *
   * @returns {boolean} True if connected and ready to send messages
   * @public
   */
  public isConnected(): boolean {
    return (
      this.state === WebSocketServiceState.CONNECTED &&
      this.ws?.readyState === WebSocket.OPEN
    );
  }

  /**
   * Connect to the WebSocket server
   *
   * Establishes WebSocket connection with timeout handling and proper
   * event listener setup. Returns a promise that resolves when the
   * connection is established and the server confirms session setup.
   *
   * @returns {Promise<ConnectionEstablishedMessage>} Promise resolving to connection confirmation
   * @throws {Error} If connection fails or times out
   * @public
   */
  public async connect(): Promise<ConnectionEstablishedMessage> {
    if (this.state === WebSocketServiceState.CONNECTING) {
      throw new Error('Connection already in progress');
    }

    return new Promise((resolve, reject) => {
      this.pendingConnectReject = reject;
      try {
        this.cleanup();
        this.setState(WebSocketServiceState.CONNECTING);
        this.isManualDisconnect = false;

        // The session id is a load-balancer routing hint only (see
        // engineRouting.ts); authentication stays in start_session.
        this.ws = new WebSocket(
          withEngineRoutingKey(this.config.wsUrl, this.config.sessionId)
        );

        // Set connection timeout
        this.connectionTimeout = setTimeout(() => {
          if (this.ws?.readyState !== WebSocket.OPEN) {
            this.ws?.close();
            this.setState(WebSocketServiceState.ERROR);
            if (this.pendingConnectReject) {
              this.pendingConnectReject(new Error('WebSocket connection timeout'));
              this.pendingConnectReject = null;
            }
          }
        }, WEBSOCKET_CONSTANTS.CONNECTION_TIMEOUT_MS);

        this.ws.onopen = () => {
          this.clearConnectionTimeout();
          wsLogger.info('WebSocket connected successfully');
          
          // Track connection success
          wsLogger.info('WebSocket connection tracked', {
            labels: {
              reconnectAttempts: this.reconnectAttempts.toString(),
            }
          });
          
          this.sendStartSession();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: SafePassageWebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);

            if (message.type === 'connection_established') {
              this.reconnectAttempts = 0;
              this.recoverableInterruptionActive = false;
              this.setState(WebSocketServiceState.CONNECTED);
              this.sessionId = message.session_id;
              this.pendingConnectReject = null;
              resolve(message);
            }
          } catch (error) {
            wsLogger.error('Error parsing WebSocket message:', error instanceof Error ? error : new Error(`${error}`));
            this.callbacks.onError?.({
              type: 'error',
              message: 'Failed to parse server message',
              code: 'PARSE_ERROR',
            });
          }
        };

        this.ws.onerror = () => {
          this.clearConnectionTimeout();
          wsLogger.error('WebSocket error occurred', {
            labels: {
              errorType: 'websocket_error',
            }
          });

          // Track connection error
          analyticsService.trackError('network', {
            errorType: 'websocket_error',
            state: this.state,
            reconnectAttempts: this.reconnectAttempts,
          });
          
          this.setState(WebSocketServiceState.ERROR);
          const errorObj = new Error('WebSocket connection error');
          this.callbacks.onConnectionError?.(errorObj);
          if (this.pendingConnectReject) {
            this.pendingConnectReject(errorObj);
            this.pendingConnectReject = null;
          }
        };

        this.ws.onclose = (event) => {
          this.clearConnectionTimeout();
          this.cleanup();
          const wasConnecting =
            this.state === WebSocketServiceState.CONNECTING;

          wsLogger.info(
            `WebSocket disconnected: ${event.code} ${event.reason}`
          );
          
          // Track disconnection
          wsLogger.info('WebSocket disconnection tracked', {
            labels: {
              code: event.code.toString(),
              reason: event.reason || 'unknown',
              wasClean: event.wasClean.toString(),
              isManualDisconnect: this.isManualDisconnect.toString(),
            }
          });
          
          const isTerminalAuthError =
            event.code === 4001 || event.code === 4003 || event.code === 4009;
          const isSessionExpired = event.code === 4002;
          const isTerminalError = isTerminalAuthError || isSessionExpired;

          if (isTerminalError) {
            wsLogger.warn(
              `Terminal WebSocket error: ${event.code} ${event.reason || 'unknown'}`
            );
            this.setState(WebSocketServiceState.ERROR);

            const error = isSessionExpired
              ? new SessionExpiredError('Session has expired')
              : new SessionAuthError(
                  `Authentication failed: ${event.reason || 'unknown'}`
                );

            this.callbacks.onConnectionError?.(error);

            if (this.pendingConnectReject && wasConnecting) {
              this.pendingConnectReject(error);
              this.pendingConnectReject = null;
            }
            return;
          }

          if (this.pendingConnectReject && wasConnecting && !this.isManualDisconnect) {
            const error = new Error(
              `WebSocket closed before connection established: ${event.code} ${event.reason || 'unknown'}`
            );
            this.setState(WebSocketServiceState.ERROR);
            this.callbacks.onConnectionError?.(error);
            this.pendingConnectReject(error);
            this.pendingConnectReject = null;
            if (this.shouldReconnect()) {
              this.scheduleReconnect();
            }
            return;
          }

          // Track as network error if unexpected disconnect
          if (!this.isManualDisconnect && event.code !== 1000) {
            analyticsService.trackError('network', {
              errorType: 'websocket_unexpected_disconnect',
              code: event.code,
              reason: event.reason || 'unknown',
            });
          }

          if (
            !this.isManualDisconnect &&
            event.code !== 1000 &&
            this.shouldReconnect()
          ) {
            this.scheduleReconnect();
          } else {
            this.setState(WebSocketServiceState.DISCONNECTED);
          }
        };
      } catch (error) {
        this.setState(WebSocketServiceState.ERROR);
        const err = error instanceof Error ? error : new Error(`${error}`);
        if (this.pendingConnectReject) {
          this.pendingConnectReject(err);
          this.pendingConnectReject = null;
        } else {
          reject(err);
        }
      }
    });
  }

  /**
   * Send a video frame to the server
   *
   * Sends base64-encoded video frame data to the server for real-time
   * analysis. Frames are typically sent at 10 FPS for optimal performance.
   *
   * @param {string} frameData - Base64 encoded video frame data
   * @public
   */
  public sendFrame(frameData: string): void {
    if (!this.isConnected()) {
      wsLogger.warn('Cannot send frame: WebSocket not connected');
      return;
    }

    try {
      this.ws?.send(
        JSON.stringify({
          type: 'video_frame',
          data: frameData,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      wsLogger.error('Error sending frame:', error instanceof Error ? error : new Error(`${error}`));
      this.callbacks.onError?.({
        type: 'error',
        message: 'Failed to send video frame',
        code: 'FRAME_SEND_ERROR',
      });
    }
  }


  /**
   * Request session statistics
   *
   * Requests current session statistics from the server.
   *
   * @public
   */
  public getStats(): void {
    if (!this.isConnected()) return;

    try {
      this.ws?.send(
        JSON.stringify({
          type: 'get_stats',
        })
      );
    } catch (error) {
      wsLogger.error('Error requesting stats:', error instanceof Error ? error : new Error(`${error}`));
    }
  }

  /**
   * Stop the current session
   *
   * Sends stop session message to server to cleanly terminate
   * the verification session.
   *
   * @public
   */
  public stopSession(): void {
    if (!this.isConnected()) return;

    try {
      this.ws?.send(
        JSON.stringify({
          type: 'stop_session',
        })
      );
    } catch (error) {
      wsLogger.error('Error stopping session:', error instanceof Error ? error : new Error(`${error}`));
    }
  }

  /**
   * Disconnect from the WebSocket server
   *
   * Cleanly disconnects from the server and cleans up all resources.
   * Sets manual disconnect flag to prevent automatic reconnection.
   *
   * @public
   */
  public disconnect(): void {
    wsLogger.info('Manual disconnect requested');
    this.isManualDisconnect = true;
    
    // Send stop_session before cleanup
    if (this.isConnected()) {
      try {
        this.stopSession();
        // Give the message a chance to send
        setTimeout(() => {
          this.cleanup();
          this.setState(WebSocketServiceState.DISCONNECTED);
        }, 100);
      } catch (error) {
        wsLogger.error('Error during disconnect:', error instanceof Error ? error : new Error(`${error}`));
        this.cleanup();
        this.setState(WebSocketServiceState.DISCONNECTED);
      }
    } else {
      this.cleanup();
      this.setState(WebSocketServiceState.DISCONNECTED);
    }
  }

  /**
   * Update callbacks
   *
   * Updates the callback functions for WebSocket events.
   *
   * @param {Partial<WebSocketServiceCallbacks>} callbacks - New callback functions
   * @public
   */
  public updateCallbacks(callbacks: Partial<WebSocketServiceCallbacks>): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  // Private methods

  private setState(newState: WebSocketServiceState): void {
    this.state = newState;
  }

  private clearConnectionTimeout(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
  }

  private shouldReconnect(): boolean {
    return (
      this.config.autoReconnect !== false &&
      this.reconnectAttempts <
        (this.config.maxReconnectAttempts ??
          WEBSOCKET_CONSTANTS.MAX_RECONNECT_ATTEMPTS)
    );
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimeout) {
      return;
    }

    // Check if we should attempt reconnection BEFORE incrementing
    if (!this.shouldReconnect()) {
      wsLogger.warn('Max reconnection attempts reached, stopping reconnection');
      this.setState(WebSocketServiceState.ERROR);
      this.callbacks.onConnectionError?.(
        this.recoverableInterruptionActive
          ? new PipelineRecoveryExhaustedError(this.reconnectAttempts)
          : new Error('Max reconnection attempts reached')
      );
      return;
    }

    this.setState(WebSocketServiceState.RECONNECTING);
    this.reconnectAttempts++;

    // Fixed 3-second retry interval
    const totalDelay = WEBSOCKET_CONSTANTS.RECONNECT_DELAY_MS;

    wsLogger.info(
      `Scheduling reconnect attempt ${this.reconnectAttempts}/${this.config.maxReconnectAttempts ?? WEBSOCKET_CONSTANTS.MAX_RECONNECT_ATTEMPTS} in ${Math.round(totalDelay)}ms`
    );
    this.callbacks.onReconnecting?.(this.reconnectAttempts);
    // The callback may have ended the connection (for example, face processing
    // was interrupted and the screen hands the outcome to Portal). A timer
    // armed now would reopen a connection nothing owns any more.
    if (this.isManualDisconnect) return;

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      if (this.isManualDisconnect) return;
      this.connect().catch((error) => {
        wsLogger.error(
          `Reconnection attempt ${this.reconnectAttempts} failed:`,
          error
        );
        // Schedule next attempt only if we haven't exceeded max attempts
        this.scheduleReconnect();
      });
    }, totalDelay);
  }

  private sendStartSession(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    this.verificationResultDelivered = false;

    const message = {
      type: 'start_session',
      emblem_state: this.config.config?.emblem_state || `service-${Date.now()}`,
      session_id: this.config.sessionId, // Include portal sessionId for backend consistency
      session_token: this.config.sessionToken,
      workflow_attempt_id: this.config.workflowAttemptId,
      workflow_attempt_generation: this.config.workflowAttemptGeneration,
      workflow_attempt_version: this.config.workflowAttemptVersion,
      operation_id: this.config.operationId,
      operation_generation: this.config.operationGeneration,
      config_overrides: this.config.config || {},
    };

    wsLogger.debug('Sending start_session with portal sessionId:', {
      labels: {
        sessionId: this.config.sessionId
          ? `${this.config.sessionId.substring(0, 8)}...`
          : 'none',
      }
    });

    this.ws.send(JSON.stringify(message));
  }

  private handleMessage(message: SafePassageWebSocketMessage): void {


    switch (message.type) {
      case 'connection_established':
        wsLogger.info('Connection established message received', {
          labels: {
            sessionId: (message as ConnectionEstablishedMessage).session_id
          }
        });
        this.callbacks.onConnectionEstablished?.(
          message as ConnectionEstablishedMessage
        );
        break;

      case 'feedback':
        this.callbacks.onFeedback?.(message as FeedbackWebSocketMessage);
        break;

      case 'capture_progress':
        this.callbacks.onCaptureProgress?.(message as CaptureProgressMessage);
        break;

      case 'next_capture_in':
        this.callbacks.onNextCaptureCountdown?.(message as NextCaptureMessage);
        break;

      case 'zone_transition':
        wsLogger.debug(
          `Zone transition: ${(message as ZoneTransitionMessage).message} to zone: ${(message as ZoneTransitionMessage).zone}`
        );
        this.callbacks.onZoneTransition?.(message as ZoneTransitionMessage);
        break;

      case 'capture_started': {
        wsLogger.debug('Capture started');
        const captureMessage = message as CaptureStartedMessage;
        if (captureMessage.zone_info) {
          wsLogger.debug('Zone info received:', { labels: { zone_info: JSON.stringify(captureMessage.zone_info) }});
        }
        this.callbacks.onCaptureStarted?.(captureMessage);
        break;
      }

      case 'processing_started':
        this.callbacks.onProcessingStarted?.(message as ProcessingMessage);
        // Signal to pause frame capture during processing
        this.callbacks.onStopFrameCapture?.();
        break;

      case 'processing_progress':
        this.callbacks.onProcessingProgress?.(message as ProcessingMessage);
        break;

      case 'verification_result': {
        if (this.verificationResultDelivered) {
          wsLogger.warn('Ignoring duplicate verification result');
          break;
        }
        this.verificationResultDelivered = true;
        const result = sanitizeVerificationResultForClient(message);
        const source =
          this.config.sessionId &&
          this.config.lifecycleVersion !== undefined &&
          this.config.workflowAttemptId &&
          this.config.workflowAttemptGeneration !== undefined &&
          this.config.workflowAttemptVersion !== undefined &&
          this.config.operationId &&
          this.config.operationGeneration !== undefined
            ? {
                sessionId: this.config.sessionId,
                expectedLifecycleVersion: this.config.lifecycleVersion,
                attemptId: this.config.workflowAttemptId,
                attemptGeneration: this.config.workflowAttemptGeneration,
                expectedAttemptVersion: this.config.workflowAttemptVersion,
                operationId: this.config.operationId,
                operationGeneration: this.config.operationGeneration,
              }
            : null;
        this.callbacks.onVerificationResult?.(
          attachVerificationOperationContext(result, source)
        );
        // Signal to stop frame capture on verification complete
        this.callbacks.onStopFrameCapture?.();
        break;
      }

      case 'session_stopped':
      case 'capture_stopped':
        this.callbacks.onSessionStopped?.(message as SessionStoppedMessage);
        // Signal to stop frame capture when session/capture ends
        this.callbacks.onStopFrameCapture?.();
        break;

      case 'disconnecting':
        this.callbacks.onDisconnecting?.(message as DisconnectingMessage);
        break;

      case 'error':
        this.callbacks.onError?.(message as ErrorMessage);
        break;

      case 'recoverable_error':
        if (!isSelfieHandoffUnavailableMessage(message)) {
          wsLogger.warn('Ignoring invalid recoverable pipeline message');
          break;
        }

        this.recoverableInterruptionActive = true;
        this.callbacks.onRecoverableError?.(message);
        this.callbacks.onStopFrameCapture?.();
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.close(1012, 'Temporary verification interruption');
        }
        break;


      default:
        wsLogger.warn('Received unknown message:', message);
    }
  }


  private registerUnloadHandler(): void {
    if (this.hasRegisteredUnloadHandler) {
      return;
    }

    // Single pagehide handler - most reliable for cleanup
    window.addEventListener('pagehide', () => {
      console.log('[WebSocket] pagehide triggered - cleanup starting');
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log('[WebSocket] Sending stop_session and closing connection');
        this.ws.send(JSON.stringify({ type: 'stop_session' }));
        this.ws.close(1000);
      } else {
        console.log('[WebSocket] No active connection to cleanup');
      }
    });
    
    this.hasRegisteredUnloadHandler = true;
  }

  private cleanup(): void {
    // Prevent recursive cleanup
    if (this.isCleaningUp) {
      wsLogger.debug('Cleanup already in progress, skipping');
      return;
    }

    this.isCleaningUp = true;
    wsLogger.debug('Starting WebSocket cleanup');

    try {
      // Clear all timeouts and intervals

      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout);
        this.reconnectTimeout = null;
      }

      this.clearConnectionTimeout();

      // Close WebSocket connection
      if (this.ws) {
        if (this.ws.readyState === WebSocket.OPEN) {
          // Try to send stop_session before closing
          try {
            this.ws.send(JSON.stringify({
              type: 'stop_session',
              reason: 'cleanup'
            }));
          } catch (error) {
            wsLogger.debug('Failed to send stop_session during cleanup:', error instanceof Error ? error : new Error(`${error}`));
          }
        }
        
        this.ws.close(1000, 'Service cleanup');
        this.ws = null;
      }

      // Reset session state
      this.sessionId = null;
    } finally {
      this.isCleaningUp = false;
      wsLogger.debug('WebSocket cleanup completed');
    }
  }

}
